import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { FiActivity, FiWifi, FiShield, FiTrendingUp, FiClock, FiCheckSquare, FiZap } from 'react-icons/fi';
import MetricCard from './MetricCard';
import SafeIcon from '../common/SafeIcon';

const SignalMonitor = ({ stats, connectionInfo }) => {
  const { ping, jitter, packetLoss, stability, history, dailyAverages } = stats;

  const chartOption = useMemo(() => ({
    backgroundColor: 'transparent',
    grid: { top: 10, right: 0, bottom: 0, left: 0, containLabel: false },
    xAxis: { type: 'category', data: history.map(h => h.time), show: false, boundaryGap: false },
    yAxis: { type: 'value', show: false, min: 0, max: (val) => val.max + 20 },
    series: [{
      data: history.map(h => h.ping),
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: { width: 2, color: '#10b981' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(16,185,129,0.2)' },
            { offset: 1, color: 'rgba(16,185,129,0)' }
          ]
        }
      }
    }]
  }), [history]);

  const getStatus = (val, thresholds) => {
    if (val <= thresholds[0]) return 'good';
    if (val <= thresholds[1]) return 'warning';
    return 'danger';
  };

  return (
    <div className="space-y-4">
      {/* Real-time Row */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard 
          title="Live Latency" 
          value={ping} 
          unit="ms" 
          icon={FiActivity} 
          status={getStatus(ping, [40, 80])} // Tighter 5G thresholds
          subtext={`Jitter: ${jitter}ms`} 
        />
        <MetricCard 
          title="Packet Loss" 
          value={packetLoss} 
          unit="%" 
          icon={FiShield} 
          status={packetLoss === 0 ? 'good' : packetLoss < 2 ? 'warning' : 'danger'} 
        />
      </div>

      {/* Daily Averages Row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 rounded-xl bg-white/5 border border-white/5">
          <div className="flex items-center gap-1.5 text-muted mb-1">
            <SafeIcon icon={FiClock} size={10} />
            <span className="text-[8px] font-black uppercase tracking-tighter">Day Latency</span>
          </div>
          <div className="text-sm font-mono font-bold text-text">{dailyAverages.globalLatency}ms</div>
        </div>
        <div className="p-3 rounded-xl bg-white/5 border border-white/5">
          <div className="flex items-center gap-1.5 text-muted mb-1">
            <SafeIcon icon={FiTrendingUp} size={10} />
            <span className="text-[8px] font-black uppercase tracking-tighter">Day Jitter</span>
          </div>
          <div className="text-sm font-mono font-bold text-text">{dailyAverages.globalJitter}ms</div>
        </div>
        <div className="p-3 rounded-xl bg-white/5 border border-white/5">
          <div className="flex items-center gap-1.5 text-muted mb-1">
            <SafeIcon icon={FiCheckSquare} size={10} />
            <span className="text-[8px] font-black uppercase tracking-tighter">Day Score</span>
          </div>
          <div className="text-sm font-mono font-bold text-primary">{dailyAverages.globalScore}/100</div>
        </div>
      </div>

      {/* Main Graph Card */}
      <div className="glass-panel rounded-xl p-4 border border-white/5 relative overflow-hidden">
        {connectionInfo.is5G && (
          <div className="absolute top-0 right-0 p-2">
             <div className="flex items-center gap-1 bg-primary/20 text-primary text-[8px] font-black px-2 py-1 rounded-bl-xl border-l border-b border-primary/30 uppercase tracking-widest animate-pulse">
               <SafeIcon icon={FiZap} size={10} /> 5G Active
             </div>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-sm font-semibold text-text flex items-center gap-2">
              <FiWifi className="text-primary" /> Live Signal Health 
            </h3>
            <p className="text-xs text-muted mt-1 font-mono">
              {connectionInfo.type} • {connectionInfo.effective}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted uppercase">Live Score</div>
            <div className={`text-xl font-bold ${stability > 85 ? 'text-primary' : stability > 60 ? 'text-warning' : 'text-danger'}`}>
              {stability}/100
            </div>
          </div>
        </div>
        <div className="h-24 w-full">
          {history.length > 1 ? (
            <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-muted">Synchronizing 5G stream...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignalMonitor;